import React, { useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { ICoupon } from '../../types/leaderboardTypes'
import { RewardItem, WalletBalanceResponse } from '../../services/rewardsService'
import { DrawerHeader } from '../../pages/eater/group/styled/ChatDrawer.styled'
import ExpiryLabel from './ExpiryLabel'
import CloseButton from '../ui/button/CloseButton'

interface CouponDetailProps {
  coupon: ICoupon | null
  reward: RewardItem | null
  onClose: () => void
  onGetCoupon: () => void
  type?: 'my-coupon' | 'redeem-points'
  walletBalance?: WalletBalanceResponse | null
  onShowInstructions?: (reward: RewardItem) => void
}

const CouponDetail: React.FC<CouponDetailProps> = ({
  coupon,
  reward,
  onClose,
  onGetCoupon,
  type,
  walletBalance,
  onShowInstructions,
}) => {
  const [isExpired, setIsExpired] = useState(false)
  
  // Use reward data if available, otherwise fall back to coupon
  const displayData = reward
    ? {
        image: reward.imageUrl, // Use actual imageUrl from API (can be null)
        title: reward.name,
        description: reward.shortDescription ?? '',
        usageInstructions: reward.usageInstructions,
        pointsRequired: reward.pointsCost,
        amountCents: reward.amountCents,
        currency: reward.currency,
        expiryDate: coupon?.expiryDate,
      }
    : coupon
      ? {
          image: coupon.image,
          title: coupon.title,
          description: coupon.description,
          pointsRequired: coupon.pointsRequired,
          amountCents: undefined,
          currency: undefined,
          expiryDate: coupon.expiryDate,
        }
      : null

  if (!displayData) return null

  // Compute button enabled state + helper text for rewards
  const canRedeem = reward?.canRedeem ?? true
  const walletAvailable = (walletBalance?.walletAvailableCents ?? 0)
  const hasEnoughCredits = walletAvailable >= (reward?.amountCents ?? 0)
  const isButtonDisabled = isExpired || !canRedeem || !hasEnoughCredits
  
  let helperText: string | null = null

  if (type === 'redeem-points' && reward) {
    if (reward.disableReason === 'SUBSCRIPTION_REQUIRED') {
      // Cash-out requires a subscription; this takes precedence over credit-based messaging.
      // Gift cards remain redeemable on the free tier.
      helperText = 'Cash out with Explorer or VIP. Upgrade to cash out, or redeem a gift card instead.'
    } else if (!hasEnoughCredits) {
      if (walletAvailable === 0) {
        helperText = 'No credits available yet. Earn points to unlock credits.'
      } else {
        helperText = 'Not enough credits in your Rewards Wallet'
      }
    } else if (!canRedeem) {
      if (reward.disableReason === 'NO_WALLET_CREDITS') {
        helperText = 'You have reached your monthly redemption limit for your plan.'
      } else {
        helperText = 'You cannot redeem this reward at the moment.'
      }
    }
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'grey.300',
        }}
      >
        <DrawerHeader>
          <Typography variant="body2">Coupon Details</Typography>

          <CloseButton color="bordered" onClick={onClose} />
        </DrawerHeader>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {displayData.image && (
          <Box
            component="img"
            src={displayData.image}
            alt={displayData.title}
            sx={{
              border: 1,
              borderColor: 'grey.200',
              borderRadius: '16px',
              boxShadow: '0px 0px 2px 0px #1A1A1A40',
              width: '100%',
              filter: isExpired ? 'grayscale(100%)' : 'none',
              aspectRatio: 5 / 2.9,
              objectFit: 'cover',
            }}
          />
        )}
        {type === 'my-coupon' && displayData.expiryDate && (
          <Box mt={2} sx={{ alignSelf: 'flex-start' }}>
            <ExpiryLabel date={displayData.expiryDate} setExpired={setIsExpired} />
          </Box>
        )}

        <Box mt={2} width="100%">
          <Typography variant="h4" maxWidth={'100%'} noWrap fontWeight={600}>
            {displayData.title}
          </Typography>
          {displayData.description && (
            <Typography
              variant="body1"
              color="text.secondary"
              my={1}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {displayData.description}
            </Typography>
          )}
        </Box>

        {type === 'redeem-points' && displayData.amountCents != null && displayData.amountCents > 0 && (
          <Box
            sx={{
              py: 1,
              mb: 2,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Estimated value: ${(displayData.amountCents / 100).toFixed(2)} {displayData.currency}
            </Typography>
          </Box>
        )}
        {type === 'redeem-points' && reward?.usageInstructions && onShowInstructions && (
          <Box mb={2}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                onShowInstructions(reward)
              }}
              sx={{ px: 0, textTransform: 'none', minWidth: 'auto' }}
            >
              How to redeem
            </Button>
          </Box>
        )}

        {coupon?.termsAndConditions && coupon.termsAndConditions.length > 0 && (
          <Box
            width="100%"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Typography sx={{ flexShrink: 0 }} variant="subtitle1">
              Terms & Conditions
            </Typography>

            <Box
              component="ul"
              sx={{
                pl: 3,
                color: 'grey.600',
                mt: 1,
                overflowY: 'auto',
                flexGrow: 1,
                minHeight: 0,
              }}
            >
              {coupon.termsAndConditions.map((item: string, index) => (
                <Box component="li" key={index}>
                  <Typography
                    variant="subtitle1"
                    component="span"
                    color="grey.600"
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        p={2}
        sx={{
          borderTop: 1,
          borderColor: 'grey.300',
          alignSelf: 'flex-end',
          width: '100%',
        }}
      >
        <Button
          variant="contained"
          onClick={onGetCoupon}
          disabled={isButtonDisabled}
          fullWidth
        >
          {reward?.disableReason === 'SUBSCRIPTION_REQUIRED'
            ? 'Upgrade to cash out'
            : isButtonDisabled
              ? 'Cannot Redeem'
              : 'Redeem'}
        </Button>
        {helperText && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            {helperText}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default CouponDetail
